import React, { FunctionComponent, useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { Popover } from 'antd-v5';
import { TFunction } from 'i18next';
import {
  ROUTER_ACCOUNT,
  ROUTER_ASC_ORDERS,
  ROUTER_BACKOFFICE_PROMOTION,
  ROUTER_BACKOFFICE_R1_NETWORK,
  ROUTER_ESTIMATE,
  ROUTER_HISTORY,
  ROUTER_HOME,
  ROUTER_MOTRIO_ECOSYSTEM,
  ROUTER_MY_NETWORK,
  ROUTER_MY_NETWORK_CLIENTS_TAB,
  ROUTER_MY_NETWORK_ORDERS_TAB,
  ROUTER_ORDERS,
  ROUTER_SETTINGS_GENERAL,
} from 'app/AppRouter';
import { trackAppEvent } from 'app/AppTracker';
import {
  BamMotrioIcon,
  ClockIcon,
  FileFolderIcon,
  FolderOpenRegularIcon,
  GlobeOutlined,
  HomeIcon,
  ListCheckIcon,
  MotrioIcon,
  ParametersIcon,
  PromoIcon,
  ReplyIcon,
  RoadMotorwayIcon,
  UserGroupIcon,
  UserIcon,
  WarehouseIcon,
} from 'assets/icons';
import { FirstHelpPopin } from 'components/Help/FirstHelpPopin';
import { getLinkItems } from 'components/Page/Header/TopHeader/Links/LinkTabs';
import { HEADER_HEIGHT } from 'components/Page/Header/TopHeader/TopHeader';
import EstimatesIconComponent from 'components/Page/PageSideBar/components/EstimatesIconComponent';
import NotificationsIconComponent from 'components/Page/PageSideBar/components/NotificationsIconComponent';
import R1SidebarMenu from 'components/Page/PageSideBar/R1SidebarMenu/R1SidebarMenu';
import { useShowR1SidebarMenu } from 'components/Page/PageSideBar/R1SidebarMenu/utils';
import SideBarCarCatalog from 'components/Page/PageSideBar/SideBarCarCatalog';
import SideBarUniversalProductsCatalog from 'components/Page/PageSideBar/SideBarUniversalProductsCatalog';
import ProfileInfo from 'components/ProfileInfo';
import UserNotifications from 'components/UserNotifications';
import {
  getAscOrdersEnabled,
  getClaimsEnabled,
  getDepositReturnListEnabled,
  getExadisReturnListEnabled,
  getTrophyAdvantagesLink,
  getVirtualStoreBamLink,
} from 'domains/appContext/AppContext.store';
import { getLastSearchedVehicleKey, getLastVehicleDetail } from 'domains/catalog/Catalog.store';
import { getCurrentPromotionsData } from 'domains/promotion/Promotion.store';
import { getUserCountry, getUserRights, UserRole } from 'domains/user';
import {
  CLAIMS_TAB,
  DEPOSIT_RETURN_LIST_TAB,
  EXADIS_RETURN_LIST_TAB,
  TAB_PARAM,
} from 'pages/AscOrdersPage/AscOrdersPage';
import { theme } from 'styles';
import { Flex, Icon, IconType, Layout, MarginBox, Text, WithTooltip } from 'UI';
import { getCondArrayItem, getData, hasUserAnyRight, isMotrioEcosystemAllowed, isVirtualStoreBamAllowed } from 'utils';
import {
  TRACKING_EVENT_GO_TO_ESTIMATE_VIA_MENU,
  TRACKING_EVENT_ORDERS_IN_PROGRESS,
} from 'utils/eventTracker/EventTracker.types';
import { Country } from 'utils/i18n/Country';
import { IconWrapper, MenuItem, SideMenuScrollWrapper } from './PageSideBar.styled';

const { Sider: AntSider } = Layout;

export const SIDEBAR_WIDTH = 250;
export const SIDEBAR_WIDTH_COLLAPSED = 90;
export const PAGE_SIDE_BAR_ID = 'page_side_bar';

// eslint-disable-next-line react/display-name
const menuIcon = (icon: IconType, color?: string) => (props: IconComponentProps) => (
  <Icon IconComponent={icon} size={props.size} display={'block'} mr={'auto'} ml={'auto'} color={color ?? props.color} />
);

const r1MenuIcon = (IconComponent: IconType) => (
  <Icon IconComponent={IconComponent} size={20} color={theme.color.brand_black} display={'block'} />
);

interface IconComponentProps {
  size: number;
  color: string;
  badgeSize: number;
  badgeOffset: [number | string, number | string];
  icon?: IconType;
  hoverFill?: string;
  disableTooltip?: boolean;
}

type MenuItemsType = {
  label: string;
  MenuIcon: FunctionComponent<IconComponentProps>;
  link?: string;
  externalLink?: string;
  requiresVehicle?: boolean;
  onMouseEnter?: (t: TFunction, vehicleKey?: string) => void;
  onDisabledClick?: (t: TFunction, vehicleKey?: string) => void;
  additionalAction?: () => void;
  'data-cy'?: string;
  key?: string;
  popinOverlay?: React.ReactNode;
  r1MenuIcon?: React.ReactNode;
  children?: R1MenuChildItemType[];
};

type R1MenuChildItemType = {
  label: string;
  link?: string;
  externalLink?: string;
  'data-cy'?: string;
  key?: string;
  r1MenuIcon?: React.ReactNode;
  children?: R1MenuChildItemType[];
};

const getUrlParams = (type: 'estimate' | 'claims' | 'exadis-return-list' | 'deposit-return-list') => {
  const params = new URLSearchParams(location.search);
  switch (type) {
    case 'estimate': {
      return params;
    }
    case 'claims':
      params.set(TAB_PARAM, CLAIMS_TAB);
      return params;
    case 'exadis-return-list':
      params.set(TAB_PARAM, EXADIS_RETURN_LIST_TAB);
      return params;
    case 'deposit-return-list':
      params.set(TAB_PARAM, DEPOSIT_RETURN_LIST_TAB);
      return params;
  }
};

export const getMenuItems = (
  t: TFunction,
  userRights: UserRole[],
  userCountry?: string,
  trophyAdvantagesUrl?: string,
  virtualStoreBamUrl?: string,
  ascOrdersEnabled = true,
  claimsEnabled = true,
  exadisReturnListEnabled = true,
  depositReturnListEnabled = true,
) => {
  const menuItems: (MenuItemsType | boolean)[] = [
    {
      label: t('common.home', 'Home'),
      MenuIcon: menuIcon(HomeIcon),
      link: ROUTER_HOME,
      'data-cy': 'tab-home',
      key: 'tab-home',
      r1MenuIcon: r1MenuIcon(HomeIcon),
    },
    {
      label: t('profile', 'Profile'),
      MenuIcon: menuIcon(UserIcon),
      link: ROUTER_ACCOUNT,
      'data-cy': 'tab-profile-settings',
      key: 'tab-profile-settings',
      r1MenuIcon: r1MenuIcon(UserIcon),
    },
    {
      label: t('common.notifications', 'Notifications'),
      MenuIcon: NotificationsIconComponent,
      'data-cy': 'tab-notifications',
      key: 'tab-notifications',
      popinOverlay: <UserNotifications />,
      r1MenuIcon: <NotificationsIconComponent size={20} />,
    },
    {
      label: t('catalog.history', 'Search History'),
      MenuIcon: menuIcon(ClockIcon),
      link: ROUTER_HISTORY,
      'data-cy': 'tab-search-history',
      key: 'tab-search-history',
    },
    ...getCondArrayItem(
      hasUserAnyRight([UserRole.COMMAND, UserRole.CONNECT_COMMANDE, UserRole.R1_ORDER_AND_CLAIM], userRights) && {
        label: t('order.my_orders', 'My Orders'),
        MenuIcon: menuIcon(FolderOpenRegularIcon),
        link: ROUTER_ORDERS,
        additionalAction: () => trackAppEvent(TRACKING_EVENT_ORDERS_IN_PROGRESS),
        'data-cy': 'tab-my-orders',
        key: 'tab-my-orders',
        r1MenuIcon: r1MenuIcon(FileFolderIcon),
      },
    ),
    ...getCondArrayItem(
      hasUserAnyRight([UserRole.R1, UserRole.R1_ORDER_AND_CLAIM], userRights) &&
        (ascOrdersEnabled || claimsEnabled) && {
          label: t('sidebar.r1.as_drive', 'AS Drive'),
          MenuIcon: menuIcon(RoadMotorwayIcon),
          link: ROUTER_ASC_ORDERS,
          'data-cy': 'tab-as-drive',
          key: 'tab-as-drive',
          r1MenuIcon: r1MenuIcon(RoadMotorwayIcon),
          children: [
            ...getCondArrayItem(
              ascOrdersEnabled && {
                label: t('sidebar.r1.asc_orders', 'ASC Orders'),
                link: ROUTER_ASC_ORDERS,
                'data-cy': 'tab-as-drive-asc-orders',
                key: 'tab-as-drive-asc-orders',
                r1MenuIcon: r1MenuIcon(ListCheckIcon),
              },
            ),
            ...getCondArrayItem(
              claimsEnabled && {
                label: t('sidebar.r1.claims', 'Claims'),
                link: `${ROUTER_ASC_ORDERS}?${getUrlParams('claims')}`,
                'data-cy': 'tab-as-drive-claims',
                key: 'tab-as-drive-claims',
                r1MenuIcon: r1MenuIcon(FileFolderIcon),
              },
            ),
            ...getCondArrayItem(
              ascOrdersEnabled &&
                claimsEnabled &&
                exadisReturnListEnabled && {
                  label: t('sidebar.r1.exadis_return_list', 'Return List'),
                  link: `${ROUTER_ASC_ORDERS}?${getUrlParams('exadis-return-list')}`,
                  dataCy: 'tab-as-drive-exadis-return-list',
                  key: 'tab-as-drive-exadis-return-list',
                  r1MenuIcon: r1MenuIcon(ReplyIcon),
                },
            ),
            ...getCondArrayItem(
              ascOrdersEnabled &&
                claimsEnabled &&
                depositReturnListEnabled && {
                  label: t('sidebar.r1.deposit_return_list', 'Deposit Return'),
                  link: `${ROUTER_ASC_ORDERS}?${getUrlParams(DEPOSIT_RETURN_LIST_TAB)}`,
                  dataCy: 'tab-as-drive-deposit-return-list',
                  key: 'tab-as-drive-deposit-return-list',
                  r1MenuIcon: r1MenuIcon(ReplyIcon),
                },
            ),
          ],
        },
    ),
    ...getCondArrayItem(
      hasUserAnyRight([UserRole.R1], userRights) && {
        label: t('order.my_network', 'My Network'),
        MenuIcon: menuIcon(GlobeOutlined),
        link: ROUTER_MY_NETWORK,
        'data-cy': 'tab-my-network',
        key: 'tab-my-network',
        r1MenuIcon: r1MenuIcon(UserGroupIcon),
        children: [
          {
            label: t('sidebar.r1.my_network_clients', 'My Network Clients'),
            link: ROUTER_MY_NETWORK_CLIENTS_TAB,
            'data-cy': 'tab-my-network-clients',
            key: 'tab-my-network-clients',
            r1MenuIcon: r1MenuIcon(UserGroupIcon),
          },
          {
            label: t('sidebar.r1.my_network_orders', 'My Network Orders'),
            link: ROUTER_MY_NETWORK_ORDERS_TAB,
            'data-cy': 'tab-my-network-orders',
            key: 'tab-my-network-orders',
            r1MenuIcon: r1MenuIcon(FileFolderIcon),
          },
          {
            label: t('sidebar.r1.my_network_promotions', 'My Network Promotions'),
            link: ROUTER_BACKOFFICE_PROMOTION,
            'data-cy': 'tab-my-network-promotions',
            key: 'tab-my-network-promotions',
            r1MenuIcon: r1MenuIcon(PromoIcon),
          },
          {
            label: t('sidebar.r1.my_network_settings', 'My Network Settings'),
            link: ROUTER_BACKOFFICE_R1_NETWORK,
            'data-cy': 'tab-my-network-settings',
            key: 'tab-my-network-settings',
            r1MenuIcon: r1MenuIcon(ParametersIcon),
          },
        ],
      },
    ),
    ...getCondArrayItem(
      hasUserAnyRight([UserRole.COMMAND, UserRole.CONNECT_COMMANDE], userRights) && {
        label: t('estimate', 'Estimate'),
        MenuIcon: EstimatesIconComponent,
        link: `${ROUTER_ESTIMATE}?${getUrlParams('estimate')}`,
        additionalAction: () => trackAppEvent(TRACKING_EVENT_GO_TO_ESTIMATE_VIA_MENU),
        'data-cy': 'tab-estimate',
        key: 'tab-estimate',
        r1MenuIcon: <EstimatesIconComponent size={20} />,
      },
    ),
    ...getCondArrayItem(
      isMotrioEcosystemAllowed(userCountry, userRights) && {
        label: t('link.motrio_ecosystem', 'Motrio Ecosystem'),
        MenuIcon: menuIcon(MotrioIcon),
        link: ROUTER_MOTRIO_ECOSYSTEM,
        'data-cy': 'tab-motrio-ecosystem',
        key: 'tab-motrio-ecosystem',
      },
    ),
    ...getCondArrayItem(
      isVirtualStoreBamAllowed(userCountry, userRights) &&
        !!virtualStoreBamUrl && {
          label: t('link.virtual_store_bam', 'Virtual Store BAM'),
          MenuIcon: menuIcon(BamMotrioIcon, 'none'),
          externalLink: virtualStoreBamUrl,
          'data-cy': 'link-virtual-store-bam',
          key: 'link-virtual-store-bam',
          r1MenuIcon: r1MenuIcon(WarehouseIcon),
        },
    ),
    ...getCondArrayItem(
      hasUserAnyRight([UserRole.R1], userRights) && {
        label: t('common.settings', 'Settings'),
        MenuIcon: menuIcon(ParametersIcon),
        link: ROUTER_SETTINGS_GENERAL,
        'data-cy': 'tab-settings',
        key: 'tab-settings',
        r1MenuIcon: r1MenuIcon(ParametersIcon),
      },
    ),
  ];
  return menuItems as MenuItemsType[];
};

const TopHeaderLinks = ({ collapsed, isActive }: { collapsed: boolean; isActive: (link: string) => boolean }) => {
  const { t } = useTranslation();
  const country = useSelector(getUserCountry);
  const promotionsData = useSelector(getCurrentPromotionsData);
  const showCurrentPromotions = promotionsData?.length > 0;
  const showTires = Country.canShowTiresByCountry(country);
  const linkItems = getLinkItems(t, showTires, showCurrentPromotions, 'sidebar');

  return (
    <>
      {!collapsed && (
        <MarginBox mr={50}>
          <Text type={'h5_bold'} disableGutter>
            {t('sidebar.shop_by_department.title', 'Shop by Department')}
          </Text>
        </MarginBox>
      )}
      {linkItems.map((item) => (
        <WithTooltip key={item.text} title={collapsed && <Flex padding={5}>{item.text}</Flex>} placement={'right'}>
          <div>
            <RenderMenuItem
              collapsed={collapsed}
              label={item.text}
              MenuIcon={menuIcon(item.icon)}
              isSelected={isActive(item.link)}
              link={item.link}
              onClick={() => {
                return;
              }}
            />
          </div>
        </WithTooltip>
      ))}
    </>
  );
};

export interface PageSideBarProps {
  collapsed: boolean;
  opened: boolean;
  activeUrl: string;
  'data-cy'?: string;
}

interface RenderMenuProps {
  collapsed: boolean;
  label: string;
  MenuIcon: FunctionComponent<IconComponentProps>;
  onClick: () => void;
  isSelected: boolean;
  link?: string;
  externalLink?: string;
  isDisabled?: boolean;
  onDisabledClick?: () => void;
  onMouseEnter?: () => void;
  'data-cy'?: string;
}

const RenderMenuItem = ({
  collapsed,
  label,
  MenuIcon,
  onClick,
  isSelected,
  link,
  externalLink,
  isDisabled,
  onDisabledClick,
  'data-cy': dataCy,
}: RenderMenuProps) => {
  const history = useHistory();

  const handleClick = () => {
    if (externalLink) {
      window.open(externalLink, '_blank', 'noopener,noreferrer');
      return;
    }
    if (!link) return;
    if (!isDisabled) {
      if (onClick) {
        onClick();
      }
      if (link) {
        history.push(link);
      }
    } else {
      if (!onDisabledClick) return;
      onDisabledClick();
    }
  };
  const collapsedItemTextType = isDisabled ? 'light_dimmer' : 'light_12_black_85';
  const itemTextType = isDisabled ? 'text_disabled' : 'text_dim';

  return (
    <MenuItem collapsed={collapsed} $active={isSelected} onClick={handleClick} data-cy={dataCy}>
      {collapsed ? (
        <MenuIcon size={24} color={theme.color.brand_black} badgeSize={17} badgeOffset={[-5, 24]} disableTooltip />
      ) : (
        <IconWrapper>
          <MenuIcon size={24} color={theme.color.brand_black} badgeSize={15} badgeOffset={[-5, 13]} disableTooltip />
        </IconWrapper>
      )}
      {!collapsed && (
        <Text className={'menu-item-title'} type={collapsed ? collapsedItemTextType : itemTextType} cursor={'pointer'}>
          {label}
        </Text>
      )}
    </MenuItem>
  );
};

const MainMenuLinks = ({ collapsed, isActive }: { collapsed: boolean; isActive: (link?: string) => boolean }) => {
  const { t } = useTranslation();
  const userRights = useSelector(getUserRights);
  const vehicleKey = useSelector(getLastSearchedVehicleKey);
  const trophyAdvantagesUrl = useSelector(getTrophyAdvantagesLink);
  const virtualStoreBamUrl = useSelector(getVirtualStoreBamLink);
  const userCountry = useSelector(getUserCountry);
  const ascOrdersEnabled = useSelector(getAscOrdersEnabled) ?? false;
  const claimsEnabled = useSelector(getClaimsEnabled) ?? false;
  const exadisReturnListEnabled = useSelector(getExadisReturnListEnabled) ?? false;
  const depositReturnListEnabled = useSelector(getDepositReturnListEnabled) ?? false;
  const menuItems = getMenuItems(
    t,
    getData(userRights) ?? [],
    userCountry,
    trophyAdvantagesUrl,
    virtualStoreBamUrl,
    ascOrdersEnabled,
    claimsEnabled,
    exadisReturnListEnabled,
    depositReturnListEnabled,
  );
  const [update, setUpdate] = useState<boolean>(false);

  return (
    <FirstHelpPopin streamId={ROUTER_HOME} popinId={`${ROUTER_HOME}_menu`} placement={'right'}>
      <ProfileInfo mini={collapsed} />
      {menuItems.map((item, key) => (
        <Popover
          key={`${key}_${item.label}`}
          content={
            item.popinOverlay ||
            (collapsed && (
              <Flex>
                <Text type="base" color="white">
                  {item.label}
                </Text>
              </Flex>
            ))
          }
          placement={item.popinOverlay ? 'rightBottom' : 'right'}
          color={item.popinOverlay ? theme.color.white : theme.color.grey15}
          trigger={item.popinOverlay ? 'click' : 'hover'}
          showArrow={!item.popinOverlay}
        >
          <div>
            <RenderMenuItem
              collapsed={collapsed}
              label={item.label}
              MenuIcon={item.MenuIcon}
              onClick={() => {
                setUpdate(!update);
                item.additionalAction?.();
              }}
              isSelected={isActive(item.link)}
              link={item.link}
              externalLink={item.externalLink}
              isDisabled={item?.requiresVehicle && !vehicleKey}
              onDisabledClick={() => (item.onDisabledClick ? item.onDisabledClick(t, vehicleKey) : undefined)}
              data-cy={item['data-cy']}
            />
          </div>
        </Popover>
      ))}
    </FirstHelpPopin>
  );
};

const PageSideBar = ({ opened, collapsed, activeUrl, 'data-cy': dataCy }: PageSideBarProps) => {
  const showR1Menu = useShowR1SidebarMenu();
  const vehicleDetail = useSelector(getLastVehicleDetail);

  const [windowHeight, setWindowHeight] = useState<number>(window.innerHeight);

  const [touchEnabled, setTouchEnabled] = useState<boolean>(false);

  const setNewWindowHeight = () => {
    setWindowHeight(window.innerHeight);
  };

  useEffect(() => {
    const enableTouchScrolling = () => {
      if (!touchEnabled) {
        setTouchEnabled(true);
      }
    };
    window.addEventListener('resize', setNewWindowHeight);
    window.addEventListener('touchstart', enableTouchScrolling);
    return () => {
      window.removeEventListener('resize', setNewWindowHeight);
      window.removeEventListener('touchstart', enableTouchScrolling);
    };
  }, [touchEnabled, setTouchEnabled]);

  const isActive = useCallback(
    (link?: string): boolean => {
      if (!link) {
        return false;
      }
      const linkSubstring = link.split('?')[0];
      if (link === '/') {
        return activeUrl === link;
      }
      return activeUrl.startsWith(linkSubstring);
    },
    [activeUrl],
  );

  const menuWidth = opened ? (collapsed ? `${SIDEBAR_WIDTH_COLLAPSED}px` : `${SIDEBAR_WIDTH}px`) : '0';
  const menuHeight = collapsed ? '100vh' : `${windowHeight - HEADER_HEIGHT + 1}px`;

  useEffect(() => {
    document.documentElement.style.setProperty('--page-side-bar-width', menuWidth);
    return () => {
      document.documentElement.style.removeProperty('--page-side-bar-width');
    };
  }, [menuWidth]);

  return (
    <AntSider width={menuWidth} id={PAGE_SIDE_BAR_ID}>
      <SideMenuScrollWrapper
        width={menuWidth}
        height={menuHeight}
        touchEnabled={touchEnabled}
        offset={HEADER_HEIGHT}
        opened={opened}
        data-cy={dataCy}
      >
        {showR1Menu ? (
          <R1SidebarMenu collapsed={collapsed} />
        ) : (
          <>
            <MainMenuLinks collapsed={collapsed} isActive={isActive} />
            <TopHeaderLinks collapsed={collapsed} isActive={isActive} />
          </>
        )}
        <SideBarUniversalProductsCatalog />
        {!collapsed && vehicleDetail && <SideBarCarCatalog vehicleDetail={vehicleDetail} />}
        <Flex />
      </SideMenuScrollWrapper>
    </AntSider>
  );
};

export default PageSideBar;
